import React from 'react';
import PropTypes from 'prop-types';
import { alphabet } from '../../utils';

const displayAlphabet = ['#', ...alphabet];

const AlphabetNavigation = ({ classes, scroll, alphabeticalMap, activeLetter, setActiveLetter }) => {
  const getTouchedLetter = ({ clientX: xPos, clientY: yPos }) => {
    const element = document.elementFromPoint(xPos, yPos);

    if (!element) {
      return '';
    }

    if (element.classList.contains(classes.letterWrapper)) {
      return element.dataset.letter;
    }

    if (
      element.classList.contains(classes.alphabetLetter) ||
      element.classList.contains(classes.alphabetLetterActive)
    ) {
      const [letter] = element.getElementsByTagName('span');
      return letter.dataset.letter;
    }

    return '';
  };

  const [selectedLetter, setSelectedLetter] = React.useState('');

  const alphabetRef = React.useRef(null);

  const onTouchMove = (e) => {
    e.preventDefault();
    const letter = getTouchedLetter(e.touches[0]);
    if (letter) {
      setSelectedLetter(letter);
    }
    return false;
  };

  React.useEffect(() => {
    // to prevent unpredicted behaviour on IOS devices (passive:false)
    if (alphabetRef.current) {
      alphabetRef.current.addEventListener('touchmove', onTouchMove, { passive: false });
    }

    return () => {
      if (alphabetRef.current) {
        alphabetRef.current.removeEventListener('touchmove', onTouchMove);
      }
    };
  }, [alphabetRef]);

  const onTouchStart = (e) => {
    const letter = getTouchedLetter(e.changedTouches[0]);
    if (letter) {
      setSelectedLetter(letter);
    }
  };

  const onTouchEnd = (e) => {
    setSelectedLetter('');
    const letter = getTouchedLetter(e.changedTouches[0]);
    if (letter) {
      setActiveLetter(letter);
      scroll(letter);
    }
  };

  const isLetterActive = (letter) => {
    return activeLetter === letter.toUpperCase();
  };

  const isLetterSelected = (letter) => {
    return selectedLetter === letter.toUpperCase();
  };

  if (!Object.keys(alphabeticalMap).length) {
    return '';
  }

  return (
    <div
      data-testid="alphabet"
      onTouchEnd={onTouchEnd}
      ref={alphabetRef}
      onTouchStart={onTouchStart}
      className={classes.alphabetWrapper}
    >
      {displayAlphabet.map((letter) => {
        if (alphabeticalMap[letter] === null) {
          return (
            <div role="button" tabIndex="0" key={letter} className={classes.disabledAlphabetLetter}>
              <span>{letter.toUpperCase()}</span>
            </div>
          );
        }
        return (
          <div
            role="button"
            tabIndex="0"
            key={letter}
            className={isLetterActive(letter) ? classes.alphabetLetterActive : classes.alphabetLetter}
            onMouseUp={() => scroll(letter)}
          >
            {isLetterSelected(letter) && <div className={classes.alphabetLetterHint}>{letter.toUpperCase()}</div>}
            <span className={classes.letterWrapper} data-letter={letter}>
              {letter.toUpperCase()}
            </span>
          </div>
        );
      })}
    </div>
  );
};

AlphabetNavigation.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  scroll: PropTypes.func,
  alphabeticalMap: PropTypes.objectOf(PropTypes.number),
  activeLetter: PropTypes.string,
  setActiveLetter: PropTypes.func,
};

AlphabetNavigation.defaultProps = {
  classes: {},
  alphabeticalMap: {},
  scroll: () => null,
  activeLetter: 'A',
  setActiveLetter: () => null,
};

export default AlphabetNavigation;
